import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, Link2, Plus, Trash2 } from 'lucide-react';
import type { GuestRole, ShareLinkInfo } from '@kaba/shared';
import { api } from '../../lib/api';
import { queryClient } from '../../lib/board';
import { toast } from '../../lib/toast';
import { Button, cx } from '../ui/ui';

export const GUEST_ROLES: [GuestRole, string, string][] = [
  ['viewer', 'Ansehen', 'Board und Karten ansehen'],
  ['commenter', 'Kommentieren', 'Ansehen und kommentieren'],
  ['editor', 'Bearbeiten', 'Karten anlegen, bearbeiten und verschieben'],
];
const roleName = (r: GuestRole) => GUEST_ROLES.find(([v]) => v === r)?.[1] ?? r;
const fmt = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' });
const field = 'min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-base sm:min-h-10 sm:text-sm';

export const shareLinksKey = (boardId: string) => ['share-links', boardId];

export function useShareLinks(boardId: string, enabled: boolean, version: number) {
  return useQuery({
    queryKey: [...shareLinksKey(boardId), version],
    queryFn: () => api.get<ShareLinkInfo[]>(`/api/boards/${boardId}/share-links`),
    enabled,
  });
}

async function copy(url: string) {
  try {
    await navigator.clipboard.writeText(url);
    toast.info('Gastlink kopiert.');
  } catch {
    toast.info('Kopieren nicht möglich – Link markieren und kopieren.');
  }
}

/** Gastlinks verwalten (nur Besitzer) */
export function ShareLinksSection({ boardId, links }: { boardId: string; links: ShareLinkInfo[] | undefined }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState<GuestRole>('commenter');
  const [expires, setExpires] = useState('30');
  const refresh = () => queryClient.invalidateQueries({ queryKey: shareLinksKey(boardId) });

  return (
    <section className="flex flex-col gap-2" aria-labelledby="share-title">
      <h3 id="share-title" className="text-sm font-medium">
        Gastzugang per Link
      </h3>
      <p className="text-xs text-[var(--ink-soft)]">
        Für Personen ohne Konto: Wer den Link öffnet, gibt nur einen Namen an und ist sofort im Board. Wer ein Konto hat, tritt damit angemeldet bei.
        Löschen oder Ablauf beendet den Zugang aller, die darüber gekommen sind.
      </p>

      {!!links?.length && (
        <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
          {links.map((l) => {
            const expired = l.expiresAt && new Date(l.expiresAt) < new Date();
            return (
              <li key={l.id} className="flex flex-col gap-2 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <Link2 size={16} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{l.name}</p>
                    <p className="truncate text-xs text-[var(--ink-soft)]">
                      {l.memberCount === 1 ? '1 Person' : `${l.memberCount} Personen`} ·{' '}
                      {expired ? (
                        <span className="text-rose-600">abgelaufen</span>
                      ) : l.expiresAt ? (
                        `gültig bis ${fmt.format(new Date(l.expiresAt))}`
                      ) : (
                        'ohne Ablauf'
                      )}
                    </p>
                  </div>
                  <select
                    aria-label={`Rechte für „${l.name}“`}
                    value={l.role}
                    className={field}
                    onChange={async (e) => {
                      const next = e.target.value as GuestRole;
                      try {
                        await api.patch(`/api/share-links/${l.id}`, { role: next });
                        toast.info(`„${l.name}“: ${roleName(next)}${l.memberCount ? ' – gilt auch für alle, die schon beigetreten sind' : ''}.`);
                        void refresh();
                      } catch (err) {
                        toast.error((err as Error).message);
                      }
                    }}
                  >
                    {GUEST_ROLES.map(([v, label]) => (
                      <option key={v} value={v}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {expired ? (
                    <Button
                      onClick={async () => {
                        await api.patch(`/api/share-links/${l.id}`, { expiresInDays: 30 });
                        void refresh();
                      }}
                    >
                      Um 30 Tage verlängern
                    </Button>
                  ) : (
                    <Button onClick={() => void copy(l.url)} disabled={!l.url}>
                      <Copy size={15} /> Link kopieren
                    </Button>
                  )}
                  <Button
                    aria-label={`Gastlink „${l.name}“ löschen`}
                    onClick={async () => {
                      const who = l.memberCount ? ` ${l.memberCount === 1 ? 'Die Person' : `Alle ${l.memberCount} Personen`}, die darüber beigetreten ${l.memberCount === 1 ? 'ist, verliert' : 'sind, verlieren'} den Zugang.` : '';
                      if (!window.confirm(`Gastlink „${l.name}“ löschen?${who}`)) return;
                      await api.del(`/api/share-links/${l.id}`);
                      void refresh();
                    }}
                  >
                    <Trash2 size={15} /> Löschen
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {adding ? (
        <form
          className="flex flex-col gap-3 rounded-xl border border-[var(--line)] p-3"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              const l = await api.post<ShareLinkInfo>(`/api/boards/${boardId}/share-links`, {
                name: name.trim(),
                role,
                expiresInDays: expires ? Number(expires) : null,
              });
              setAdding(false);
              setName('');
              void refresh();
              await copy(l.url);
            } catch (err) {
              toast.error((err as Error).message);
            }
          }}
        >
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Für wen?
            <input className={field} value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} placeholder="z. B. Agentur Müller" autoFocus />
          </label>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1 text-sm font-medium">Rechte</legend>
            {GUEST_ROLES.map(([v, label, hint]) => (
              <label key={v} className={cx('flex min-h-11 items-center gap-3 rounded-lg border px-3 py-2', role === v ? 'border-accent bg-accent-soft/50' : 'border-[var(--line)]')}>
                <input type="radio" name="share-role" checked={role === v} onChange={() => setRole(v)} className="size-4 accent-[var(--color-accent)]" />
                <span className="flex flex-col">
                  <span className="text-sm font-medium">{label}</span>
                  <span className="text-xs text-[var(--ink-soft)]">{hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Gültig
            <select className={field} value={expires} onChange={(e) => setExpires(e.target.value)}>
              <option value="7">7 Tage</option>
              <option value="30">30 Tage</option>
              <option value="90">90 Tage</option>
              <option value="365">1 Jahr</option>
              <option value="">unbegrenzt</option>
            </select>
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={() => setAdding(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={!name.trim()}>
              Link erstellen und kopieren
            </Button>
          </div>
        </form>
      ) : (
        <Button className="self-start" onClick={() => setAdding(true)}>
          <Plus size={16} /> Neuer Gastlink
        </Button>
      )}
    </section>
  );
}
